import { Component, Input, OnInit, Output, EventEmitter, ChangeDetectorRef, ViewChild, Inject } from '@angular/core';
import { RequestRoot, USE_VERIFICATION_DATABASE, USE_VERIFICATION_EAC, USE_VERIFICATION_UPPER_MRZ } from 'types';
import { AppContext } from '../../base/control/context';
import { FeaturesImpl } from '../../base/model/features-impl';
import { DocumentField } from '../../base/model/features-decl';
import { MRZVisibleState, ScanMRZState } from '../scan-mrz/pass.scan.mrz.component';
import { ScanChipMessage, PassScanChipComponent } from '../scan-chip/pass.scan.chip.component';
import { PassportStatusSOD } from '../../service/passport-reader.service';
import { PassportData, ActivationSetting, VerificationSetting } from './passportData';

export enum PassVerifyState {
  opened,
  invisible,
  exitable,
}

export interface PassVerifyResult {
  optionalFieldsOk: boolean;
  mandatoryFieldsOk: boolean;
  signaturesOk: boolean;
  photoOk: boolean;
  number: string;
  photo: string;
}

class VerificationErrors {
  photoHash: boolean | null;
  typeD: boolean | null;
  typeP: boolean | null;
  country: boolean | null;
  lastName: boolean | null;
  firstName: boolean | null;
  serial: boolean | null;
  nationality: boolean | null;
  birthDate: boolean | null;
  gender: boolean | null;
  validdate: boolean | null;
}

export interface PassVerifyPayload {
  requestData: PassportData;
  printedData: PassportData;
  chipData: PassportData;
  wronglist: VerificationErrors;
  condition: boolean[];
  isDg1AndDg2Valid: boolean;
  isDg3Valid: boolean;
  isMissingDg3: boolean;
  isMissingEAC: boolean;
  isMissingFingerprints: boolean;
  isSkippedFingerprints: boolean;
}

@Component({
  selector: 'i-pass-verify',
  templateUrl: './pass.verify.component.html',
  styleUrls: ['./pass.verify.component.scss'],
})
export class PassVerifyComponent implements OnInit {
  @Input() request: RequestRoot | null = null;
  @Input() activationSettings: ActivationSetting = new ActivationSetting();
  @Input() verificationSettings: VerificationSetting = new VerificationSetting();
  @Output() loadingEvent = new EventEmitter<boolean>();
  @Output() PassVerifyState = new EventEmitter<PassVerifyState>();
  @Output() isPassVerificationSuccess = new EventEmitter<PassVerifyResult>();
  @Output() scanMRZStateChange = new EventEmitter<ScanMRZState>();

  @ViewChild(PassScanChipComponent) private passScanChipComponent: PassScanChipComponent;

  condition = [false, false, false];
  MRZ2: string | null = null;
  ChipMessage: string = '';
  ChipMessageError: boolean = false;
  MRZstate: string = 'closed';
  chipIsWorking: boolean = false;
  canRestartChip: boolean = false;
  isDg1AndDg2Valid: boolean | null = null;
  isDg3Valid: boolean | null = null;
  isMissingDg3: boolean = false;
  isMissingEAC: boolean = false;
  isMissingFingerprints: boolean = false;
  isSkippedFingerprints: boolean = false;
  chipData = new PassportData();
  requestData = new PassportData();
  printedData = new PassportData();
  wronglist = new VerificationErrors();

  constructor(
    public appContext: AppContext,
    private cdRef: ChangeDetectorRef,
    @Inject(USE_VERIFICATION_DATABASE) public useVerificationDatabase: boolean,
    @Inject(USE_VERIFICATION_UPPER_MRZ) private useVerificationUpperMRZ: boolean,
    @Inject(USE_VERIFICATION_EAC) public useVerificationEAC: boolean,
  ) {}

  get totalClass() {
    if (this.isDg1AndDg2Valid && (!this.useVerificationEAC || this.isMissingDg3 || this.isDg3Valid)) {
      return 'verification-container-success';
    }
    if (this.isDg1AndDg2Valid && this.isMissingEAC) {
      return 'verification-container-warning';
    }
    return 'verification-container-error';
  }

  get fingerprintsMessage() {
    if (this.isMissingDg3) {
      return 'VERIFICATION.final.fingerprints.missing';
    }
    if (this.isMissingEAC) {
      return 'VERIFICATION.final.fingerprints.disabled';
    }
    if (!this.isDg3Valid) {
      return 'VERIFICATION.final.fingerprints.invalid';
    }
    if (this.isMissingFingerprints) {
      return 'VERIFICATION.final.fingerprints.missing';
    }
    if (this.isSkippedFingerprints) {
      return 'VERIFICATION.final.fingerprints.skipped';
    }
    return 'VERIFICATION.final.fingerprints.valid';
  }

  activationSetting(params: ActivationSetting | string) {
    const type = typeof params;

    if (params && type === 'object' && Object.keys(params).length !== 0) {
      for (let [key, value] of Object.entries(params)) {
        this.activationSettings[key] = value;
      }
    }

    if (params && type === 'string') {
      this.activationSettings.code = params as string;
    }
  }

  verificationSetting(params: VerificationSetting | string) {
    const type = typeof params;

    if (params && type === 'object' && Object.keys(params).length !== 0) {
      for (let [key, value] of Object.entries(params)) {
        this.verificationSettings[key] = value;
      }
    }
  }

  exportPayload(): PassVerifyPayload {
    return {
      requestData: this.requestData,
      printedData: this.printedData,
      chipData: this.chipData,
      wronglist: this.wronglist,
      condition: this.condition,
      isDg1AndDg2Valid: this.isDg1AndDg2Valid!,
      isDg3Valid: this.isDg3Valid!,
      isMissingDg3: this.isMissingDg3,
      isMissingEAC: this.isMissingEAC,
      isMissingFingerprints: this.isMissingFingerprints,
      isSkippedFingerprints: this.isSkippedFingerprints,
    };
  }

  importPayload(payload: PassVerifyPayload) {
    this.requestData = payload.requestData;
    this.printedData = payload.printedData;
    this.chipData = payload.chipData;
    this.wronglist = payload.wronglist;
    this.condition = payload.condition;
    this.isDg1AndDg2Valid = payload.isDg1AndDg2Valid;
    this.isDg3Valid = payload.isDg3Valid;
    this.isMissingDg3 = this.isMissingDg3;
    this.isMissingEAC = this.isMissingEAC;
    this.isMissingFingerprints = this.isMissingFingerprints;
    this.isSkippedFingerprints = this.isSkippedFingerprints;
  }

  /**
   * Преобразовывает год в дате в соокращенный для алгоритма верификации, т.к. в MRZ у дат неполный год (2 последних разряда)
   * @param inputDate дата в формате ДД.ММ.ГГГГ
   * @returns Дата формата ДД.ММ.ГГ
   */
  private getDateShortYear(date: string) {
    const fullYearLength = 4;
    const shortYearLength = 2;
    if (date.length < shortYearLength) {
      return date;
    }
    return date.slice(0, date.length - fullYearLength) + date.slice(-shortYearLength);
  }

  private loadDatabaseData() {
    if (this.useVerificationDatabase && this.request) {
      this.requestData.photo = FeaturesImpl.formatDocumentField(this.request, DocumentField.Photo, this.appContext);
      this.requestData.photoHash = FeaturesImpl.formatDocumentField(this.request, DocumentField.PhotoHash, this.appContext);
      this.requestData.typeD = FeaturesImpl.formatDocumentField(this.request, DocumentField.PassportType, this.appContext);
      this.requestData.typeP = FeaturesImpl.formatDocumentField(this.request, DocumentField.PassportSubType, this.appContext);
      this.requestData.country = FeaturesImpl.formatDocumentField(this.request, DocumentField.Country, this.appContext);
      this.requestData.lastName = FeaturesImpl.formatDocumentField(this.request, DocumentField.LastName, this.appContext);
      this.requestData.firstName = FeaturesImpl.formatDocumentField(this.request, DocumentField.FirstName, this.appContext);
      this.requestData.serial = FeaturesImpl.formatDocumentField(this.request, DocumentField.SerialNumber, this.appContext);
      this.requestData.nationality = FeaturesImpl.formatDocumentField(this.request, DocumentField.Nationality, this.appContext);
      this.requestData.birthDate = FeaturesImpl.formatDocumentField(this.request, DocumentField.BirthDate, this.appContext);
      this.requestData.gender = FeaturesImpl.formatDocumentField(this.request, DocumentField.Gender, this.appContext);
      this.requestData.validdate = FeaturesImpl.formatDocumentField(this.request, DocumentField.ExpiryDate, this.appContext);
    } else {
      this.requestData.photo = null;
      this.requestData.photoHash = null;
      this.requestData.typeD = null;
      this.requestData.typeP = null;
      this.requestData.country = null;
      this.requestData.lastName = null;
      this.requestData.firstName = null;
      this.requestData.serial = null;
      this.requestData.nationality = null;
      this.requestData.birthDate = null;
      this.requestData.gender = null;
      this.requestData.validdate = null;
    }
    this.condition[0] = !!this.requestData.photo && this.requestData.photo.length > 1;
    console.log(
      '%c >>>> loadDatabaseData:',
      'background:#efefef;padding:4px',
      'condition[0]:',
      this.condition[0],
      'requestData.photo:',
      this.requestData.photo,
      'requestData.photo.length:',
      this.requestData?.photo?.length,
    ); // Убрать логирование после отладки в ПСФ
  }

  private buildWrongList() {
    for (let key in this.requestData) {
      if (this.useVerificationDatabase) {
        if (key === 'birthDate' || key === 'validdate') {
          this.wronglist[key as keyof VerificationErrors] =
            this.getDateShortYear(this.requestData[key as keyof PassportData]!) !== this.printedData[key as keyof PassportData];
        } else {
          this.wronglist[key as keyof VerificationErrors] =
            this.requestData[key as keyof PassportData] != this.printedData[key as keyof PassportData] ||
            this.requestData[key as keyof PassportData] != this.chipData[key as keyof PassportData] ||
            this.printedData[key as keyof PassportData] != this.chipData[key as keyof PassportData];
        }
      } else {
        this.wronglist[key as keyof VerificationErrors] =
          this.printedData[key as keyof PassportData] != this.chipData[key as keyof PassportData];
      }
    }
    this.wronglist.photoHash = this.useVerificationDatabase ? this.requestData.photoHash != this.chipData.photoHash : false;
    console.log(
      '%c >>>> buildWrongList:',
      'background:#efefef;padding:4px',
      'wronglist:',
      this.wronglist,
      'wronglist.photoHash:',
      this.wronglist.photoHash,
      'useVerificationDatabase:',
      this.useVerificationDatabase,
      'requestData.photoHash:',
      this.requestData.photoHash,
      'chipData.photoHash:',
      this.chipData.photoHash,
    ); // Убрать логирование после отладки в ПСФ
  }

  private emitResult() {
    this.isPassVerificationSuccess.emit({
      optionalFieldsOk:
        !this.wronglist.firstName &&
        !this.wronglist.lastName &&
        !this.wronglist.birthDate &&
        !this.wronglist.country &&
        !this.wronglist.gender &&
        !this.wronglist.nationality &&
        !this.wronglist.validdate &&
        !this.wronglist.typeD &&
        !this.wronglist.typeP &&
        !this.wronglist.serial,
      mandatoryFieldsOk: !this.wronglist.firstName && !this.wronglist.lastName,
      signaturesOk: this.isDg1AndDg2Valid! && (!this.useVerificationEAC || this.isDg3Valid! || this.isMissingDg3 || this.isMissingEAC),
      photoOk: !this.wronglist.photoHash,
      number: typeof this.printedData.serial === 'string' ? this.printedData.serial : '',
      photo: typeof this.printedData.photo === 'string' ? this.printedData.photo : '',
    });
  }

  ngOnInit() {
    this.loadDatabaseData();
  }

  completeInit(r: RequestRoot | null) {
    this.request = r;
    this.loadDatabaseData();
  }

  restartOptical() {
    this.canRestartChip = false;
    this.ChipMessage = '';
    this.ChipMessageError = false;
    this.appContext.changeMessageSinkVisibleRef(false);
    this.printedData = new PassportData();
    this.MRZ2 = null;
    this.chipData = new PassportData();
    this.condition[2] = false;
    this.wronglist = new VerificationErrors();
    this.isDg1AndDg2Valid = null;
    this.isDg3Valid = null;
    this.isMissingDg3 = false;
    this.isMissingEAC = false;
    this.isMissingFingerprints = false;
    this.isSkippedFingerprints = false;
    this.loadingEvent.emit(true);
    this.MRZstate = 'visible';
  }

  restartChip() {
    this.chipData = new PassportData();
    this.condition[2] = false;
    this.canRestartChip = false;
    this.passScanChipComponent.startScaning();
  }

  restartDatabase(r: RequestRoot | null) {
    this.request = r;
    this.loadDatabaseData();
    this.buildWrongList();
    this.emitResult();
  }

  onOpticalProgress(val: MRZVisibleState) {
    switch (val) {
      case MRZVisibleState.closed: {
        this.loadingEvent.emit(false);
        this.MRZstate = 'closed';
        this.PassVerifyState.emit(PassVerifyState.opened);
        this.appContext.changeMessageSinkVisibleRef(true);
        break;
      }
      case MRZVisibleState.visible: {
        this.PassVerifyState.emit(PassVerifyState.invisible);
        this.MRZstate = 'visible';
        break;
      }
      case MRZVisibleState.invisible: {
        this.MRZstate = 'invisible';
        this.appContext.changeMessageSinkVisibleRef(true);
        this.PassVerifyState.emit(PassVerifyState.exitable);
        break;
      }
    }
  }

  onOpticalData(val: PassportData) {
    if (this.useVerificationUpperMRZ) {
      this.printedData = { ...val };
      this.condition[1] = !!this.printedData.photo && this.printedData.photo.length > 1;
    }
  }

  onOpticalResult(val: string) {
    this.MRZ2 = val;
  }

  OnScanMRZStateChange(newState: ScanMRZState) {
    if (newState === ScanMRZState.InitializationError) {
      this.MRZstate = 'invisible';
      this.loadingEvent.emit(false);
    }
    this.scanMRZStateChange.emit(newState);
  }

  onChipProgress(message: ScanChipMessage) {
    if (message.error) {
      this.chipIsWorking = false;
      this.canRestartChip = true;
    } else {
      this.chipIsWorking = true;
    }
    this.ChipMessage = message.message;
    this.ChipMessageError = message.error;
    this.cdRef.detectChanges();
  }

  onChipPseudoOpticalData(val: PassportData) {
    this.printedData = val;
    this.condition[1] = !!this.printedData.photo && this.printedData.photo.length > 1;
  }

  onChipData(val: PassportData) {
    this.chipData = val;
    this.condition[2] = !!this.chipData.photo && this.chipData.photo.length > 1;
    this.buildWrongList();
  }

  onMissingDg3() {
    this.isMissingDg3 = true;
  }

  onMissingEAC() {
    this.isMissingEAC = true;
  }

  onMissingFingerprints() {
    this.isMissingFingerprints = true;
  }

  onSkippedFingerprints() {
    this.isSkippedFingerprints = true;
  }

  onChipResult(val: PassportStatusSOD) {
    this.MRZ2 = null;
    this.chipIsWorking = false;
    this.loadingEvent.emit(false);
    this.isDg1AndDg2Valid = val.gost_bac && val.icao_bac;
    this.isDg3Valid = val.gost_eac && val.icao_eac;
    this.appContext.getMessageSink()('INFO', 'PASSPORT.VERIFICATION.COMPLETED');
    this.emitResult();
  }
}
